// ────────────────────────────────────────────────────────────────────────────
//  Shop.tsx — Premium-Shop · klar strukturiert, App-Store-Funnel-optimiert
//
//  Architektur:
//   1. Hero (kompakt — 3 Pills, kein Trust-Mass-Bombing)
//   2. App-Store-Banner — globaler Hinweis: "Käufe nur in der iOS-App"
//   3. Sticky Filter-Tabs   — Alle / Solo / Teams / Add-Ons / Vergleich
//   4. Gefiltertes Grid (uniforme Cards)
//   5. Trust-Section
//   6. FAQ + Footer-CTA
//
//  WEB_CHECKOUT_ENABLED-Flag bleibt — sobald Stripe live geht, einfach
//  flippen, der gesamte CTA-Pfad bleibt der gleiche.
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react'
import { useT, useLangCode } from '../i18n'
import { useProducts, useExtensions, type Product } from '../lib/useExtensions'
import { Icon } from '../components/Icon'

const APP_STORE_URL = 'https://apps.apple.com/app/id6766051168'
// 🚧 LIVE-RELEASE-TODO (siehe LIVE-RELEASE-CHECKLIST.md → Punkt #5):
// Auf `true` flippen sobald Stripe live geht. Vorher Backend-Webhook + Customer-
// Portal-URL aktivieren. Solange `false`: Webapp-Shop verweist alle Käufe an
// den App Store / die iOS-App.
const WEB_CHECKOUT_ENABLED = false

// ─── Visual-Mapping für die Shop-Cards (Icon, Gradient, Highlight-Reihenfolge)
type ProductVisual = {
  icon:  'building' | 'punch' | 'recruit' | 'sparkle' | 'calendar' | 'users'
  gradient:  string            // CSS-Gradient für die Hero-Strip
  glow:      string            // RGB für den Background-Glow
  badgeKey?: string            // i18n-Key für den Sticker („NEU", „BELIEBT", …)
}

// `badgeKey` ist ein i18n-Key (resolved via t() im Render). Vorher hatten wir
// hardcoded deutsche Strings hier — auf der englischen Page erschien dann
// „NUR iOS" / „BELIEBT" auf Deutsch.
const VISUALS: Record<string, ProductVisual> = {
  BusinessBasic:       { icon: 'building', gradient: 'linear-gradient(135deg,#8a44ee,#3a62ff)',  glow: '138,68,238', badgeKey: 'shop.badge.popular' },
  BusinessL:           { icon: 'building', gradient: 'linear-gradient(135deg,#3a62ff,#33d670)',  glow: '58,98,255' },
  BusinessBasicYearly: { icon: 'building', gradient: 'linear-gradient(135deg,#8a44ee,#33d670)',  glow: '138,68,238', badgeKey: 'shop.badge.upTo20' },
  BusinessLYearly:     { icon: 'building', gradient: 'linear-gradient(135deg,#3a62ff,#33d670)',  glow: '58,98,255',  badgeKey: 'shop.badge.upTo20' },
  PunchClock:          { icon: 'punch',    gradient: 'linear-gradient(135deg,#33d670,#3a62ff)',  glow: '51,214,112', badgeKey: 'shop.badge.iosOnly' },
  Recruitment:         { icon: 'recruit',  gradient: 'linear-gradient(135deg,#ff5b5c,#ffb300)',  glow: '255,91,92' },
  TerminalMode:        { icon: 'punch',    gradient: 'linear-gradient(135deg,#8a44ee,#ff5b5c)',  glow: '138,68,238' },
  ShiftPlanner:        { icon: 'calendar', gradient: 'linear-gradient(135deg,#3a62ff,#8a44ee)',  glow: '58,98,255',  badgeKey: 'shop.badge.new' },
  MoreSpace:           { icon: 'sparkle',  gradient: 'linear-gradient(135deg,#33d670,#8a44ee)',  glow: '51,214,112' },
}

const DEFAULT_VISUAL: ProductVisual = {
  icon: 'sparkle',
  gradient: 'linear-gradient(135deg,#8a44ee,#3a62ff)',
  glow: '138,68,238',
}

// ────────────────────────────────────────────────────────────────────────────
//  FILTER-TABS — was der User auswählen kann
// ────────────────────────────────────────────────────────────────────────────
type FilterId = 'all' | 'solo' | 'teams' | 'addons' | 'compare'

// ════════════════════════════════════════════════════════════════════════════
//  ROOT
// ════════════════════════════════════════════════════════════════════════════
export function Shop() {
  const t = useT()
  const lang = useLangCode()
  const { items: products, loading } = useProducts()
  const { has } = useExtensions()
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly')
  const [filter,  setFilter]  = useState<FilterId>('all')

  // Produkt-Pools nach Kategorie
  const pools = useMemo(() => {
    const businessMonthly = products.filter(p => p.slug === 'BusinessBasic'       || p.slug === 'BusinessL')
    const businessYearly  = products.filter(p => p.slug === 'BusinessBasicYearly' || p.slug === 'BusinessLYearly')
    const solo            = products.filter(p => p.slug === 'PunchClock')
    const addons          = products.filter(p =>
      p.slug === 'TerminalMode' || p.slug === 'Recruitment' || p.slug === 'ShiftPlanner' || p.slug === 'MoreSpace'
    )
    return {
      teams: billing === 'monthly' ? businessMonthly : businessYearly,
      solo,
      addons,
    }
  }, [products, billing])

  // Counts für Tab-Badges
  const counts = {
    all:    pools.solo.length + pools.teams.length + pools.addons.length,
    solo:   pools.solo.length,
    teams:  pools.teams.length,
    addons: pools.addons.length,
  }

  return (
    <div className="relative">
      <ShopOrbs />

      <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <Hero t={t} />

        {/* ── App-Store-Hinweis-Banner ─────────────────────────────────────
            Macht klar dass alle Käufe (und der Stempeluhr-Trial) über die
            iOS-App laufen. Vorher in mini-Pills versteckt, jetzt zentral.
        */}
        <AppStoreBanner t={t} />

        {/* ── Sticky Filter-Tabs ───────────────────────────────────────────
            Statt seitenlangem Scrollen kann der User direkt die Kategorie
            wählen die ihn interessiert. „Alle" ist Default und zeigt alles
            untereinander, einzelne Tabs filtern auf den jeweiligen Pool.
        */}
        <FilterBar
          filter={filter}
          onChange={setFilter}
          counts={counts}
          billing={billing}
          onBilling={setBilling}
          t={t}
        />

        {loading ? (
          <Loading />
        ) : (
          <div className="space-y-12 md:space-y-16 mt-8">

            {/* ── SOLO ──────────────────────────────────────────────── */}
            {(filter === 'all' || filter === 'solo') && pools.solo.length > 0 && (
              <CategorySection
                kicker={t('shop.sectionSoloKicker')}
                title={t('shop.sectionSolo')}
                subtitle={t('shop.sectionSoloSub')}
              >
                <div className="grid md:grid-cols-2 gap-5">
                  {pools.solo.map(p => (
                    <PricingCard
                      key={p.slug}
                      product={p}
                      active={has(p.slug)}
                      features={[
                        t('shop.feat.punchclock'),
                        t('shop.feat.privateOnly'),
                        t('shop.feat.firstMonthFree'),
                        t('shop.feat.cancelable'),
                      ]}
                      t={t}
                    />
                  ))}
                </div>
              </CategorySection>
            )}

            {/* ── TEAMS ─────────────────────────────────────────────── */}
            {(filter === 'all' || filter === 'teams') && pools.teams.length > 0 && (
              <CategorySection
                kicker={t('shop.sectionTeamKicker')}
                title={t('shop.sectionTeam')}
                subtitle={t('shop.saveYearly')}
                right={filter === 'teams' ? null : <BillingToggle value={billing} onChange={setBilling} />}
              >
                <div className="grid md:grid-cols-2 gap-5">
                  {pools.teams.map(p => (
                    <PricingCard
                      key={p.slug}
                      product={p}
                      active={has(p.slug)}
                      highlighted={p.slug === 'BusinessBasic' || p.slug === 'BusinessBasicYearly'}
                      features={businessFeatures(p, lang, t)}
                      t={t}
                    />
                  ))}
                </div>
              </CategorySection>
            )}

            {/* ── ADD-ONS ───────────────────────────────────────────── */}
            {(filter === 'all' || filter === 'addons') && pools.addons.length > 0 && (
              <CategorySection
                kicker={t('shop.sectionAddonsKicker')}
                title={t('shop.sectionAddons')}
                subtitle={t('shop.sectionAddonsSub')}
              >
                <div className="grid md:grid-cols-2 gap-5">
                  {pools.addons.map(p => (
                    <PricingCard
                      key={p.slug}
                      product={p}
                      active={has(p.slug)}
                      compact
                      features={addonFeatures(p, t)}
                      t={t}
                    />
                  ))}
                </div>
              </CategorySection>
            )}

            {/* ── VERGLEICH ─────────────────────────────────────────── */}
            {(filter === 'all' || filter === 'compare') && (
              <FeatureComparisonStrip t={t} />
            )}

            {/* ── Trust + FAQ — nur im "Alle"-View ─────────────────── */}
            {filter === 'all' && (
              <>
                <TrustSection t={t} />
                <FAQ t={t} />
              </>
            )}
          </div>
        )}

        <FooterCTA t={t} />
      </div>

      <style>{`
        @keyframes shop-orb-a { 0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(40px,-30px) scale(1.10)} }
        @keyframes shop-orb-b { 0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-30px,20px) scale(1.06)} }
        @keyframes shop-orb-c { 0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(25px,40px) scale(1.12)} }
        @keyframes shop-card-tilt { 0%,100%{transform:perspective(1000px) rotateX(0) rotateY(0) translateY(0)}50%{transform:perspective(1000px) rotateX(-2deg) rotateY(2deg) translateY(-4px)} }
        @keyframes shop-pulse-ring { 0%{box-shadow:0 0 0 0 rgba(138,68,238,0.55)}70%{box-shadow:0 0 0 14px rgba(138,68,238,0)}100%{box-shadow:0 0 0 0 rgba(138,68,238,0)} }
        @keyframes shop-shimmer-bar { 0%{background-position:-200% 0}100%{background-position:200% 0} }
        @keyframes shop-sticker-bounce { 0%,100%{transform:rotate(8deg) translateY(0)}50%{transform:rotate(8deg) translateY(-3px)} }
        .animate-shop-orb-a { animation: shop-orb-a 14s ease-in-out infinite; }
        .animate-shop-orb-b { animation: shop-orb-b 17s ease-in-out infinite; }
        .animate-shop-orb-c { animation: shop-orb-c 20s ease-in-out infinite; }
        .animate-shop-pulse-ring { animation: shop-pulse-ring 2.5s ease-out infinite; }
        .animate-shop-sticker { animation: shop-sticker-bounce 2.4s ease-in-out infinite; }
        .shop-shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: shop-shimmer-bar 3s linear infinite;
        }
      `}</style>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  HERO — kompakt, ohne Trust-Pill-Wand drüber
// ════════════════════════════════════════════════════════════════════════════
function Hero({ t }: { t: any }) {
  return (
    <header className="mb-8 md:mb-10 text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-5"
           style={{
             background: 'rgba(138,68,238,0.12)',
             border: '1px solid rgba(138,68,238,0.30)',
             color: '#8a44ee',
           }}>
        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-shop-pulse-ring" />
        {t('shop.heroKicker')}
      </div>
      <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-tight leading-[1.05]">
        <span className="heading-fade">{t('shop.title')}</span>
      </h1>
      <p className="max-w-xl mx-auto text-app-text-dim text-sm md:text-base leading-relaxed">
        {t('shop.heroSub')}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2 text-[11px] font-semibold">
        <Pill icon="shield" label={t('shop.pillDsgvo')} />
        <Pill icon="apple"  label={t('shop.pillApple')} />
        <Pill icon="check"  label={t('shop.pillCancel')} />
      </div>
    </header>
  )
}

function Pill({ icon, label }: { icon: 'shield' | 'apple' | 'check'; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass-flat text-app-text-dim">
      <Icon name={icon} size={12} />
      {label}
    </span>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  APP-STORE-BANNER — globaler Hinweis: Käufe gehen über die iOS-App
//
//  Auch der Stempeluhr-Free-Trial läuft über den App-Store — Apple verlangt
//  das, weil es technisch eine StoreKit-Subscription mit Introductory Offer
//  ist (1 Monat gratis, danach 3,99 €/Monat, jederzeit kündbar).
// ════════════════════════════════════════════════════════════════════════════
function AppStoreBanner({ t }: { t: any }) {
  return (
    <div className="relative mb-10 md:mb-12">
      <div className="absolute -inset-1 rounded-3xl blur-2xl opacity-40 pointer-events-none"
           style={{ background: 'linear-gradient(135deg,#000,#1a1a1f,#3a62ff)' }} />
      <div className="relative glass-strong rounded-3xl overflow-hidden">
        <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center p-5 md:p-6">
          {/* Apple-Logo-Bubble */}
          <div className="w-14 h-14 shrink-0 rounded-2xl grid place-items-center bg-black text-white shadow-lg">
            <Icon name="apple" size={26} className="text-white" />
          </div>
          {/* Text */}
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-widest text-app-text-mute uppercase mb-1">
              {t('shop.appStoreNoticeKicker')}
            </div>
            <h2 className="text-lg md:text-xl font-bold tracking-tight mb-1">
              {t('shop.appStoreNoticeTitle')}
            </h2>
            <p className="text-sm text-app-text-dim leading-relaxed">
              {t('shop.appStoreNoticeText')}
            </p>
          </div>
          {/* CTA */}
          <a
            href={APP_STORE_URL}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-black text-white font-semibold text-sm hover:bg-black/85 transition shadow-md whitespace-nowrap"
          >
            <Icon name="apple" size={16} className="text-white" />
            {t('shop.openAppStore')}
          </a>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  FILTER-BAR — Sticky-Tabs für Solo / Teams / Add-Ons / Vergleich
// ════════════════════════════════════════════════════════════════════════════
function FilterBar({
  filter, onChange, counts, billing, onBilling, t,
}: {
  filter:    FilterId
  onChange:  (f: FilterId) => void
  counts:    { all: number; solo: number; teams: number; addons: number }
  billing:   'monthly' | 'yearly'
  onBilling: (v: 'monthly' | 'yearly') => void
  t:         any
}) {
  const tabs: { id: FilterId; label: string; count?: number }[] = [
    { id: 'all',     label: t('shop.filterAll'),     count: counts.all    },
    { id: 'solo',    label: t('shop.filterSolo'),    count: counts.solo   },
    { id: 'teams',   label: t('shop.filterTeams'),   count: counts.teams  },
    { id: 'addons',  label: t('shop.filterAddons'),  count: counts.addons },
    { id: 'compare', label: t('shop.filterCompare')                       },
  ]

  return (
    <div className="sticky top-2 md:top-4 z-30">
      <div className="glass-strong rounded-2xl p-2 flex flex-wrap items-center gap-1.5 md:gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1">
          {tabs.map(tab => {
            const active = filter === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => onChange(tab.id)}
                className={`relative px-3 md:px-4 py-1.5 rounded-xl transition text-xs md:text-sm font-semibold ${
                  active
                    ? 'bg-accent text-white shadow-md shadow-accent/30'
                    : 'text-app-text-dim hover:text-app-text hover:bg-app-elev'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                    active ? 'bg-white/20' : 'bg-app-elev-2 text-app-text-mute'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {/* Billing-Toggle nur wenn Teams-Tab aktiv */}
        {filter === 'teams' && (
          <BillingToggle value={billing} onChange={onBilling} />
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  CATEGORY-SECTION — schmaler Wrapper mit Header + Slot
// ════════════════════════════════════════════════════════════════════════════
function CategorySection({
  kicker, title, subtitle, right, children,
}: {
  kicker?:   string
  title:     string
  subtitle?: string
  right?:    React.ReactNode
  children:  React.ReactNode
}) {
  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5 md:mb-6">
        <div>
          {kicker && (
            <div className="inline-block text-[10px] font-bold tracking-widest text-accent uppercase mb-2">
              {kicker}
            </div>
          )}
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight heading-fade">{title}</h2>
          {subtitle && <div className="text-sm text-app-text-mute mt-1">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  BILLING-TOGGLE
// ════════════════════════════════════════════════════════════════════════════
function BillingToggle({ value, onChange }: { value: 'monthly' | 'yearly'; onChange: (v: 'monthly' | 'yearly') => void }) {
  const t = useT()
  return (
    <div className="grid grid-cols-2 gap-1 p-1 rounded-xl"
         style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
      {([
        { v: 'monthly' as const, label: t('shop.monthly') },
        { v: 'yearly'  as const, label: t('shop.yearly'),  badge: t('shop.badge.upTo20Short') },
      ]).map(o => {
        const active = value === o.v
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`relative px-4 py-1.5 rounded-lg transition text-xs font-semibold ${
              active ? 'bg-accent text-white shadow-md shadow-accent/30'
                     : 'text-app-text-dim hover:text-app-text'
            }`}
          >
            {o.label}
            {(o as any).badge && active && (
              <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-white/20 font-bold">{(o as any).badge}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  PRICING-CARD — die zentrale Produkt-Karte
// ════════════════════════════════════════════════════════════════════════════
function PricingCard({
  product, active, highlighted, features, compact, t,
}: {
  product: Product
  active:      boolean
  highlighted?: boolean
  compact?:     boolean
  features:     string[]
  t:            any
}) {
  const v = VISUALS[product.slug] ?? DEFAULT_VISUAL
  const isYearly = product.billingPeriodDays === 365
  const isMonth  = product.billingPeriodDays === 30
  // PunchClock: regulär 3,99 €/Monat. Der 1-Monat-Gratis-Trial läuft NUR in
  // der iOS-/Mac-App via Apple-Introductory-Offer. Web-Checkout für PunchClock
  // ist deshalb deaktiviert — wir leiten User zum App Store. Das alte
  // „kostenlos auf unbestimmte Zeit"-Launch-Sale ist mit Mai 2026 ausgelaufen.
  const isPunchClock = product.slug === 'PunchClock'

  return (
    <div
      className={`relative rounded-[28px] overflow-hidden transition-all duration-300 ${
        highlighted ? 'glass-strong' : 'glass'
      } group/card`}
      style={highlighted ? {
        boxShadow: `0 0 0 1px rgba(${v.glow},0.45), 0 24px 60px rgba(${v.glow},0.18)`,
      } : undefined}
    >
      <div className="h-1.5 w-full" style={{ background: v.gradient }} />

      {v.badgeKey && (
        <div className="absolute top-4 right-4 z-10">
          <div className="px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-widest text-white animate-shop-sticker shadow-lg"
               style={{ background: v.gradient, boxShadow: `0 6px 18px rgba(${v.glow},0.45)` }}>
            {t(v.badgeKey)}
          </div>
        </div>
      )}

      {active && (
        <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: 'rgba(51,214,112,0.16)', color: '#33d670', border: '1px solid rgba(51,214,112,0.30)' }}>
          ✓ {t('shop.activeBadge')}
        </span>
      )}

      <div className={`p-6 ${compact ? '' : 'md:p-7'}`}>
        <div className="flex items-start gap-3 mb-4 mt-2">
          <div className="w-12 h-12 shrink-0 rounded-2xl grid place-items-center text-white"
               style={{ background: v.gradient, boxShadow: `0 6px 16px rgba(${v.glow},0.30)` }}>
            <Icon name={v.icon as any} size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl md:text-2xl font-bold tracking-tight">
              {localized(t, 'shop.productName.' + product.slug, product.name)}
            </h3>
            <div className="text-sm text-app-text-dim mt-1 leading-relaxed">
              {localized(t, 'shop.productDesc.' + product.slug, product.description)}
            </div>
          </div>
        </div>

        {/* Preis */}
        <div className="mt-5 mb-5 flex items-baseline gap-1.5 flex-wrap">
          <span className="text-4xl md:text-5xl font-bold tracking-tight"
                style={{
                  background: v.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
            {/* PunchClock: hartcodierter Fallback 3,99 € falls die API ein
                stale 0 liefert (Cache-Edge-Phase nach Preis-Update). Damit
                sehen User auf keinen Fall „0 €/Monat" auf der Karte. */}
            {formatPrice(
              product.slug === 'PunchClock' && (!product.basePrice || product.basePrice === 0)
                ? 3.99
                : (product.basePrice ?? 0)
            )}
          </span>
          {(isMonth || isYearly) && (
            <span className="text-sm text-app-text-mute font-medium ml-1">
              {isYearly ? t('shop.perYear') : t('shop.perMonth')}
            </span>
          )}
          {!isMonth && !isYearly && (
            <span className="text-[10px] text-app-text-mute uppercase font-bold tracking-wider ml-2 px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(58,98,255,0.10)', color: '#3a62ff', border: '1px solid rgba(58,98,255,0.25)' }}>
              ∞ {t('shop.oneTime')}
            </span>
          )}
        </div>

        {/* PunchClock: 1-Monat-Gratis-Trial-Hinweis — NUR über iOS-App
            (Apple StoreKit Introductory Offer). Web-Käufer würden's sonst
            nicht bekommen. */}
        {isPunchClock && (
          <div className="mb-4 -mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-extrabold"
                  style={{ background: 'rgba(51,214,112,0.14)', color: '#33d670', border: '1px solid rgba(51,214,112,0.36)' }}>
              <Icon name="sparkle" size={10} />
              {t('shop.iosTrialOnly')}
            </span>
          </div>
        )}

        <ul className={`space-y-2.5 ${compact ? 'mb-5' : 'mb-6'}`}>
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-app-text-dim">
              <span className="mt-0.5 shrink-0 grid place-items-center w-4 h-4 rounded-full"
                    style={{ background: 'rgba(51,214,112,0.18)', color: '#33d670' }}>
                <Icon name="check" size={11} />
              </span>
              <span dangerouslySetInnerHTML={{ __html: f }} />
            </li>
          ))}
        </ul>

        <BuyArea
          active={active}
          t={t}
          priceNote={product.priceFormatted}
          // PunchClock zwingt App-Store-Funnel — der Trial geht nur dort
          forceAppStore={isPunchClock}
        />
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  BUY-AREA — App-Store-Funnel (oder Web-Checkout wenn aktiv)
// ════════════════════════════════════════════════════════════════════════════
function BuyArea({
  active, t, priceNote, forceAppStore,
}: {
  active: boolean
  t: any
  priceNote: string
  forceAppStore?: boolean   // PunchClock zwingt App-Store-Pfad — Trial nur dort
}) {
  if (WEB_CHECKOUT_ENABLED && !forceAppStore) {
    return (
      <div className="space-y-2">
        <button className="btn-primary w-full justify-center">
          {active ? t('shop.openApp') : t('shop.buy')}
        </button>
        <div className="text-[10px] text-app-text-mute text-center pt-1">
          {priceNote} · {t('shop.cardSuffix')}
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <a
        href={APP_STORE_URL}
        target="_blank" rel="noreferrer"
        className="block w-full text-center py-3 rounded-xl font-semibold text-sm bg-black text-white hover:bg-black/85 hover:scale-[1.01] transition shadow-md"
      >
        <span className="inline-flex items-center gap-2 justify-center">
          <Icon name="apple" size={14} className="text-white" />
          {active
            ? t('shop.openApp')
            : forceAppStore
              ? t('shop.tryInApp')   // „Gratis in der App testen"
              : t('shop.buyOnApple')}
        </span>
      </a>
      <div className="text-[10px] text-app-text-mute text-center pt-0.5">
        {priceNote} · {t('shop.cardSuffix')}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  TRUST-SECTION
// ════════════════════════════════════════════════════════════════════════════
function TrustSection({ t }: { t: any }) {
  const items: { icon: 'shield' | 'check' | 'sparkle' | 'apple'; title: string; text: string }[] = [
    { icon: 'shield',  title: t('shop.trustPrivacy.title'),  text: t('shop.trustPrivacy.text')  },
    { icon: 'check',   title: t('shop.trustNoLockIn.title'), text: t('shop.trustNoLockIn.text') },
    { icon: 'sparkle', title: t('shop.trustUpdates.title'),  text: t('shop.trustUpdates.text')  },
    { icon: 'apple',   title: t('shop.trustApple.title'),    text: t('shop.trustApple.text')    },
  ]
  return (
    <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
      {items.map((it, i) => (
        <div key={i} className="glass p-5 rounded-2xl">
          <div className="w-10 h-10 rounded-xl grid place-items-center mb-3"
               style={{ background: 'rgba(138,68,238,0.12)', color: '#8a44ee' }}>
            <Icon name={it.icon} size={20} />
          </div>
          <div className="text-sm font-bold mb-1">{it.title}</div>
          <div className="text-xs text-app-text-mute leading-relaxed">{it.text}</div>
        </div>
      ))}
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  FEATURE-COMPARISON STRIP
// ════════════════════════════════════════════════════════════════════════════
function FeatureComparisonStrip({ t }: { t: any }) {
  const rows: { feature: string; solo: boolean; basic: boolean; large: boolean }[] = [
    { feature: t('shop.cmp.punch'),       solo: true,  basic: true,  large: true  },
    { feature: t('shop.cmp.chat'),        solo: false, basic: true,  large: true  },
    { feature: t('shop.cmp.files'),       solo: false, basic: true,  large: true  },
    { feature: t('shop.cmp.payroll'),     solo: false, basic: true,  large: true  },
    { feature: t('shop.cmp.recruit'),     solo: false, basic: false, large: false },
    { feature: t('shop.cmp.terminal'),    solo: false, basic: false, large: false },
    { feature: t('shop.cmp.shifts'),      solo: false, basic: false, large: false },
    { feature: t('shop.cmp.slots10'),     solo: false, basic: true,  large: false },
    { feature: t('shop.cmp.slots50'),     solo: false, basic: false, large: true  },
  ]
  return (
    <section>
      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <div className="inline-block text-[10px] font-bold tracking-widest text-accent uppercase mb-2">
            {t('shop.compareKicker')}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight heading-fade">{t('shop.compareTitle')}</h2>
        </div>
      </div>
      <div className="glass-strong rounded-3xl overflow-hidden">
        <div className="grid grid-cols-4 text-[11px] md:text-xs font-bold uppercase tracking-wider text-app-text-mute border-b border-app-border">
          <div className="px-4 py-3">{t('shop.compareCol.feature')}</div>
          <div className="px-2 md:px-4 py-3 text-center">{t('shop.compareCol.solo')}</div>
          <div className="px-2 md:px-4 py-3 text-center">{t('shop.compareCol.basic')}</div>
          <div className="px-2 md:px-4 py-3 text-center">{t('shop.compareCol.large')}</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className={`grid grid-cols-4 text-sm ${i % 2 === 0 ? '' : 'bg-app-elev/40'}`}>
            <div className="px-4 py-3 text-app-text">{r.feature}</div>
            <div className="px-2 md:px-4 py-3 text-center"><Tick on={r.solo}/></div>
            <div className="px-2 md:px-4 py-3 text-center"><Tick on={r.basic}/></div>
            <div className="px-2 md:px-4 py-3 text-center"><Tick on={r.large}/></div>
          </div>
        ))}
        <div className="px-4 py-3 text-[11px] text-app-text-mute border-t border-app-border">
          {t('shop.compareFootnote')}
        </div>
      </div>
    </section>
  )
}
function Tick({ on }: { on: boolean }) {
  return on ? (
    <span className="inline-grid place-items-center w-5 h-5 rounded-full"
          style={{ background: 'rgba(51,214,112,0.20)', color: '#33d670' }}>
      <Icon name="check" size={12} />
    </span>
  ) : (
    <span className="text-app-text-mute">—</span>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  FAQ
// ════════════════════════════════════════════════════════════════════════════
function FAQ({ t }: { t: any }) {
  const items = [1, 2, 3, 4, 5].map(i => ({ q: t(`shop.faq${i}.q`), a: t(`shop.faq${i}.a`) }))
  const [open, setOpen] = useState<number | null>(0)
  return (
    <section>
      <div className="mb-5">
        <div className="inline-block text-[10px] font-bold tracking-widest text-accent uppercase mb-2">
          {t('shop.faqKicker')}
        </div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight heading-fade">{t('shop.faqTitle')}</h2>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const isOpen = open === i
          return (
            <button
              key={i}
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              className="block w-full text-left glass-flat rounded-2xl px-5 py-4 hover:bg-app-elev-2 transition"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-app-text">{it.q}</span>
                <span className="text-app-text-mute text-lg shrink-0">{isOpen ? '−' : '+'}</span>
              </div>
              {isOpen && (
                <p className="mt-2 text-sm text-app-text-dim leading-relaxed">{it.a}</p>
              )}
            </button>
          )
        })}
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  FOOTER-CTA
// ════════════════════════════════════════════════════════════════════════════
function FooterCTA({ t }: { t: any }) {
  return (
    <div className="mt-16 md:mt-20 relative">
      <div className="absolute inset-0 rounded-[32px] blur-2xl opacity-50 pointer-events-none"
           style={{ background: 'linear-gradient(135deg,#8a44ee,#3a62ff,#33d670)' }} />
      <div className="relative glass-strong rounded-[32px] p-8 md:p-12 text-center overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-50 pointer-events-none"
             style={{ background: 'radial-gradient(circle, rgba(138,68,238,0.30), transparent 65%)' }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-5"
               style={{
                 background: 'rgba(51,214,112,0.15)',
                 border: '1px solid rgba(51,214,112,0.30)',
                 color: '#33d670',
               }}>
            <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />
            {t('shop.footerKicker')}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-3 heading-fade">
            {t('shop.footerTitle')}
          </h2>
          <p className="text-app-text-dim max-w-xl mx-auto mb-6">{t('shop.footerSub')}</p>
          <a
            href={APP_STORE_URL}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-3 px-7 py-4 rounded-2xl bg-black text-white font-semibold text-base hover:bg-black/85 hover:scale-[1.02] transition shadow-lg"
          >
            <Icon name="apple" size={28} className="text-white" />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-wider opacity-70">{t('shop.appStoreKicker')}</div>
              <div className="text-base font-bold leading-tight">App Store</div>
            </div>
          </a>
          <div className="mt-3 text-[11px] text-app-text-mute">{t('shop.iosOnlyHint')}</div>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  ORBS
// ════════════════════════════════════════════════════════════════════════════
function ShopOrbs() {
  return (
    <div aria-hidden className="absolute inset-x-0 top-0 h-[640px] overflow-hidden pointer-events-none">
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full opacity-50 animate-shop-orb-a"
           style={{ background: 'radial-gradient(circle, rgba(138,68,238,0.32), transparent 60%)' }} />
      <div className="absolute -top-20 -right-32 w-[560px] h-[560px] rounded-full opacity-50 animate-shop-orb-b"
           style={{ background: 'radial-gradient(circle, rgba(58,98,255,0.28), transparent 60%)' }} />
      <div className="absolute top-40 left-1/3 w-[400px] h-[400px] rounded-full opacity-40 animate-shop-orb-c"
           style={{ background: 'radial-gradient(circle, rgba(51,214,112,0.22), transparent 60%)' }} />
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  HELPERS
// ════════════════════════════════════════════════════════════════════════════
function formatPrice(value: number): string {
  if (Number.isInteger(value)) return `${value} €`
  return `${value.toString().replace('.', ',')} €`
}

function localized(t: any, key: string, fallback: string): string {
  const v = t(key)
  return v === key ? fallback : v
}

function businessFeatures(p: Product, _lang: 'de' | 'en', t: any): string[] {
  const slots = (() => {
    if (p.slug.startsWith('BusinessBasic')) return 10
    if (p.slug.startsWith('BusinessL'))     return 50
    return 0
  })()
  const isYearly = p.billingPeriodDays === 365
  return [
    t('shop.feat.employees', { n: slots }),
    t('shop.feat.orgCreate'),
    t('shop.feat.teamPunchclock'),
    t('shop.feat.payroll'),
    t('shop.feat.files'),
    t('shop.feat.chat'),
    isYearly ? t('shop.feat.yearlyDiscount') : t('shop.feat.cancelable'),
  ]
}

function addonFeatures(p: Product, t: any): string[] {
  if (p.slug === 'TerminalMode') return [
    t('shop.feat.terminal'),
    t('shop.feat.pinAuth'),
    t('shop.feat.kiosk'),
  ]
  if (p.slug === 'Recruitment') return [
    t('shop.feat.recruitment'),
    t('shop.feat.jobLinks'),
    t('shop.feat.attachments'),
  ]
  if (p.slug === 'ShiftPlanner') return [
    t('shop.feat.shiftsCreate'),
    t('shop.feat.shiftsAssign'),
    t('shop.feat.shiftsChannel'),
    t('shop.feat.shiftsView'),
  ]
  if (p.slug === 'MoreSpace') return [
    t('shop.feat.moreSpace'),
  ]
  return [p.description]
}

function Loading() {
  return (
    <div className="grid md:grid-cols-2 gap-5 mt-8">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="glass p-6 h-72 animate-pulse rounded-3xl">
          <div className="h-6 w-32 bg-app-elev-2 rounded mb-3" />
          <div className="h-4 w-48 bg-app-elev rounded mb-6" />
          <div className="h-12 w-24 bg-app-elev-2 rounded mb-5" />
          <div className="space-y-2">
            <div className="h-3 bg-app-elev rounded" />
            <div className="h-3 bg-app-elev rounded w-3/4" />
            <div className="h-3 bg-app-elev rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  )
}
